import { useMemo } from "react";
import type { InputAgent } from "@/components/panels/agent-stack";

interface UseInputBarSelectionArgs {
  agents: InputAgent[];
  /** The user's manual click-driven selection. */
  rawSelectedIds: Set<string>;
  /** Fallback when no manual selection is active (cursor mode uses Denker). */
  defaultAgentName?: string;
}

interface UseInputBarSelectionResult {
  /** Resolved selection: manual click > defaultAgentName. */
  selectedIds: Set<string>;
}

/** Resolves agent selection independently of chat text. */
export function useInputBarSelection({
  agents,
  rawSelectedIds,
  defaultAgentName,
}: UseInputBarSelectionArgs): UseInputBarSelectionResult {
  const selectedIds = useMemo(() => {
    if (rawSelectedIds.size > 0) return new Set(rawSelectedIds);
    // Fallback to a named default (cursor mode passes "Denker"). Returning
    // a fresh set here is read-only — we don't push it back into parent
    // state, so the bottom-bar mode (no defaultAgentName) keeps an empty
    // selection until the user explicitly picks one.
    if (defaultAgentName) {
      const fallback = agents.find(
        (a) => a.name.toLowerCase() === defaultAgentName.toLowerCase(),
      );
      if (fallback) return new Set([fallback.id]);
    }
    return new Set<string>();
  }, [rawSelectedIds, agents, defaultAgentName]);

  return {
    selectedIds,
  };
}
